import { useCallback, useEffect, useRef, useState } from 'react';
import useUrlQuery, { compact, scopeParams } from './useUrlQuery';

export const DEFAULT_PAGE_SIZE = 25;

/**
 * A list page's URL filters (useUrlQuery) and the rows fetched for them. fetchList(params) returns
 * { count, results }; defaults must be a constant object.
 */
export default function useListQuery(fetchList, defaults) {
  const [query, setQuery] = useUrlQuery(defaults);
  const [state, setState] = useState({ rows: [], count: 0, loading: true, error: null });
  const [reloads, setReloads] = useState(0);
  // a background refresh keeps the table as it is until the new rows arrive
  const silent = useRef(false);

  useEffect(() => {
    let current = true;
    if (!silent.current) setState((previous) => ({ ...previous, loading: true, error: null }));
    silent.current = false;
    fetchList(apiParams(query))
      .then((data) => current && setState({ rows: data.results, count: data.count, loading: false, error: null }))
      .catch((e) => current && setState((previous) => ({ ...previous, loading: false, error: e.message })));
    return () => {
      current = false;
    };
  }, [fetchList, query, reloads]);

  const patchRow = useCallback((id, changes) => {
    setState((previous) => ({
      ...previous, rows: previous.rows.map((row) => (row.id === id ? { ...row, ...changes } : row)),
    }));
  }, []);

  const refresh = useCallback(() => {
    silent.current = true;
    setReloads((value) => value + 1);
  }, []);

  return { query, setQuery, ...state, reload: () => setReloads((value) => value + 1), refresh, patchRow };
}

// URL query -> list API parameters (social/rest/ads.py)
function apiParams(query) {
  return compact({
    ...exportParams(query), page: query.page || 1, page_size: query.page_size || DEFAULT_PAGE_SIZE,
  });
}

// The list API parameters a download of the whole list takes: all but the page
export function exportParams(query) {
  const params = {
    ...scopeParams(query),
    ordering: query.ordering,
    search: query.search,
    // a status group (paused, closed), expanded by the API
    status: query.status,
    // ad accounts: enabled / disabled
    sync: query.sync,
  };
  return compact(params);
}
